// Pinterest boards: recognise a board link, read the board scraper's rows and
// lay the pins out as image cards.
//
// A board page draws its pins with JavaScript after it loads, so reading the
// page (read_url, link previews) only ever sees the board's name and pin
// count. The pins come from an Apify Actor instead (pinterest-import).
//
// Pure TypeScript with no imports: used by the pinterest-import edge function
// and Partnr's read_url (Deno), and by the app's import panel (Vite).

export const PINTEREST_IMPORT_EXTENSION_ID = 'pinterest-import';
export const PINTEREST_IMPORT_ACTION_ID = 'import_board';

/** The board scraper. Input `{ urls, maxPinsPerBoard }`, one row per pin (see normalizePinterestRows). */
export const PINTEREST_BOARD_ACTOR = 'headlessagent~pinterest-board-downloader';

export const DEFAULT_PINS = 100;
export const MAX_PINS = 200;

export interface PinterestBoardRef {
  username: string;
  slug: string;
  /** A board section, e.g. /name/board/section/. */
  section: string | null;
  /** Canonical https://www.pinterest.com/<username>/<slug>/[<section>/] with no query. */
  url: string;
}

/** First path segments that are Pinterest routes, never a username. */
const ROUTES = new Set([
  'pin', 'pins', 'ideas', 'search', 'today', 'explore', 'topics', 'categories', 'settings', 'business',
  'login', 'signup', 'logout', 'password', 'about', 'shopping', 'shop', 'videos', 'video', 'news_hub',
  'homefeed', 'notifications', 'messages', 'inbox', 'oauth', 'resource', 'edit', 'help', 'policy',
  'terms', 'privacy', 'secure', 'url_shortener', 'amp', 'board', 'boards', 'source', 'discover',
]);

/** Second path segments that are profile tabs, not boards. */
const PROFILE_TABS = new Set(['pins', 'boards', 'followers', 'following', 'likes', 'tried', 'activity', 'collages', 'more_ideas']);

export function isPinterestHost(host: string): boolean {
  return /(^|\.)pinterest\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host.toLowerCase());
}

function parse(raw: string): URL | null {
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

/** pin.it short links point at a pin or a board; only following the redirect tells which. */
export function isPinterestShortLink(raw: string): boolean {
  const url = parse(raw);
  return !!url && /^(www\.)?pin\.it$/.test(url.hostname.toLowerCase()) && url.pathname.length > 1;
}

function segment(value: string): string | null {
  let s = value;
  try {
    s = decodeURIComponent(value);
  } catch {
    // keep the raw segment
  }
  s = s.trim();
  return s && !s.startsWith('_') && /^[^/?#\s]+$/.test(s) ? s : null;
}

/**
 * A board (or board section) URL on any Pinterest domain, canonicalised to
 * www.pinterest.com with the query (invite codes, tracking) dropped. Null for
 * pins, profiles, idea pages, search, pin.it and anything else.
 */
export function pinterestBoardFromUrl(raw: string): PinterestBoardRef | null {
  const url = parse(raw);
  if (!url || !isPinterestHost(url.hostname)) return null;
  const segs = url.pathname.split('/').filter(Boolean);
  if (segs.length < 2) return null;
  const username = segment(segs[0]);
  const slug = segment(segs[1]);
  if (!username || !slug) return null;
  if (ROUTES.has(username.toLowerCase()) || PROFILE_TABS.has(slug.toLowerCase())) return null;
  const section = segs[2] ? segment(segs[2]) : null;
  const path = [username, slug, ...(section ? [section] : [])].map(encodeURIComponent).join('/');
  return { username, slug, section, url: `https://www.pinterest.com/${path}/` };
}

export type ResolvedPinterestLink =
  | { ok: true; board: PinterestBoardRef }
  | { ok: false; code: 'not_a_board' | 'single_pin' | 'unresolved'; message: string };

const NOT_A_BOARD = 'That is not a Pinterest board link. Paste a board link like pinterest.com/name/board-name/.';

/**
 * The board a link points at. pin.it short links are followed (pin.it, then
 * api.pinterest.com, then the board), only ever to Pinterest's own hosts.
 */
export async function resolvePinterestBoard(raw: string, fetchImpl: typeof fetch = fetch): Promise<ResolvedPinterestLink> {
  let current = raw.trim();
  for (let hop = 0; hop < 4; hop++) {
    const board = pinterestBoardFromUrl(current);
    if (board) return { ok: true, board };
    const url = parse(current);
    const host = url?.hostname.toLowerCase() ?? '';
    if (url && isPinterestHost(host) && /^\/pin\/[^/]+/.test(url.pathname)) {
      return { ok: false, code: 'single_pin', message: 'That link opens a single pin, not a board. Paste the link of the board it is saved to.' };
    }
    const followable = url && (isPinterestShortLink(current) || host === 'api.pinterest.com');
    if (!followable) return { ok: false, code: 'not_a_board', message: NOT_A_BOARD };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const res = await fetchImpl(url.toString(), { method: 'GET', redirect: 'manual', signal: controller.signal });
      await res.body?.cancel().catch(() => undefined);
      const location = res.headers.get('location');
      if (res.status < 300 || res.status >= 400 || !location) break;
      const next = new URL(location, url).toString();
      const nextHost = new URL(next).hostname.toLowerCase();
      if (!isPinterestHost(nextHost) && !/^(www\.)?pin\.it$/.test(nextHost)) break;
      current = next;
    } catch {
      break;
    } finally {
      clearTimeout(timer);
    }
  }
  return { ok: false, code: 'unresolved', message: "Couldn't open that Pinterest link. Paste the board's full link from the address bar instead." };
}

/** "sol-ice-cream" -> "sol ice cream", for when the scraper gives no board name. */
export function boardNameFromSlug(ref: Pick<PinterestBoardRef, 'slug' | 'section'>): string {
  const words = (s: string) => s.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return ref.section ? `${words(ref.slug)}: ${words(ref.section)}` : words(ref.slug);
}

// ── Scraper rows ─────────────────────────────────────────────────

export interface PinterestImage {
  url: string;
  width: number | null;
  height: number | null;
}

export interface PinterestPin {
  id: string;
  /** https://www.pinterest.com/pin/<id>/ */
  pinUrl: string;
  title: string;
  description: string;
  /** The size cards use: 736px wide when the scraper has it. */
  image: PinterestImage;
  /** The full-size original, when it differs from `image`. */
  originalUrl: string | null;
  /** Where the pin links out to, when it does. */
  link: string | null;
  isVideo: boolean;
  boardName: string | null;
}

type Row = Record<string, unknown>;

const asRow = (value: unknown): Row | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Row) : null;

const text = (value: unknown): string => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '');

const num = (value: unknown): number | null => {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

function httpsUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const url = parse(value);
  return url && url.protocol === 'https:' ? url.toString() : null;
}

interface SizedImage extends PinterestImage {
  /** "236x", "736x", "orig", ... */
  label: string;
}

/**
 * Every image size a row offers. Scrapers return either a list
 * (`[{ url, width, height, quality }]`) or Pinterest's own map
 * (`{ '236x': { url, width, height }, orig: {...} }`), or just one URL.
 */
function imageSizes(data: Row): SizedImage[] {
  const out: SizedImage[] = [];
  const push = (value: unknown, label: string) => {
    const r = asRow(value);
    const url = httpsUrl(r ? r.url : value);
    if (url) out.push({ url, width: r ? num(r.width) : null, height: r ? num(r.height) : null, label: label.toLowerCase() });
  };
  const images = data.images;
  if (Array.isArray(images)) {
    for (const image of images) push(image, text(asRow(image)?.quality) || text(asRow(image)?.size));
  } else if (asRow(images)) {
    for (const [label, image] of Object.entries(images as Row)) push(image, label);
  }
  if (!out.length) {
    for (const key of ['image', 'imageUrl', 'image_url', 'imageLarge', 'thumbnail']) push(data[key], key === 'imageLarge' ? 'orig' : '');
  }
  return out;
}

/** 736px wide when offered (sharp on a card, a fraction of the original's bytes), else the closest sensible size. */
export function pickPinImage(sizes: SizedImage[]): { image: PinterestImage; originalUrl: string | null } | null {
  if (!sizes.length) return null;
  const original = sizes.find((s) => s.label === 'orig' || s.label === 'originals' || /\/originals\//.test(s.url)) ?? null;
  const by736 = sizes.find((s) => s.label === '736x' || /\/736x\//.test(s.url));
  const wideEnough = sizes
    .filter((s) => s.width !== null && s.width >= 600)
    .sort((a, b) => (a.width as number) - (b.width as number))[0];
  const largest = [...sizes].sort((a, b) => (b.width ?? 0) - (a.width ?? 0))[0];
  const chosen = by736 ?? wideEnough ?? original ?? largest;
  return {
    image: { url: chosen.url, width: chosen.width, height: chosen.height },
    originalUrl: original && original.url !== chosen.url ? original.url : null,
  };
}

/**
 * The scraper's rows as pins: `{ url, data: { id, title, images, videos, board, link } }`
 * (the fields are also read from the row itself, for scrapers that do not nest).
 * Pins without an image or an id are dropped, duplicates kept once.
 */
export function normalizePinterestRows(rows: unknown): PinterestPin[] {
  if (!Array.isArray(rows)) return [];
  const seen = new Set<string>();
  const pins: PinterestPin[] = [];
  for (const value of rows) {
    const row = asRow(value);
    if (!row || row.error) continue;
    const data = asRow(row.data) ?? row;
    const idValue = data.id ?? data.pinId ?? data.pin_id;
    const id = typeof idValue === 'number' ? String(idValue) : text(idValue);
    if (!/^\d{5,25}$/.test(id) || seen.has(id)) continue;
    const picked = pickPinImage(imageSizes(data));
    if (!picked) continue;
    seen.add(id);
    const link = httpsUrl(data.link);
    pins.push({
      id,
      pinUrl: `https://www.pinterest.com/pin/${id}/`,
      title: text(data.title ?? data.gridTitle ?? data.grid_title).slice(0, 300),
      description: text(data.description ?? data.closeupDescription).slice(0, 500),
      image: picked.image,
      originalUrl: picked.originalUrl,
      link: link && !/(^|\.)pinterest\./.test(new URL(link).hostname) ? link : null,
      isVideo: !!(data.videos || data.video || data.isVideo || data.is_video),
      boardName: text(asRow(data.board)?.name) || null,
    });
  }
  return pins;
}

// ── Cards ────────────────────────────────────────────────────────

export const PIN_CARD_WIDTH = 240;
export const PIN_GAP = 16;
export const PIN_COLUMNS = 5;

/** A 240px wide card at the pin's ratio; tall pins stop at 2.5:1 like Pinterest's own grid. */
export function pinCardSize(image: Pick<PinterestImage, 'width' | 'height'>): { width: number; height: number } {
  if (!image.width || !image.height) return { width: PIN_CARD_WIDTH, height: 300 };
  const ratio = Math.min(2.5, Math.max(0.4, image.height / image.width));
  return { width: PIN_CARD_WIDTH, height: Math.round(PIN_CARD_WIDTH * ratio) };
}

/** Shortest-column-first masonry, Pinterest's own layout. */
export function pinMasonry(
  heights: number[],
  origin: { x: number; y: number },
  opts: { columns?: number; cardWidth?: number; gap?: number } = {},
): Array<{ x: number; y: number }> {
  const columns = Math.max(1, opts.columns ?? PIN_COLUMNS);
  const cardWidth = opts.cardWidth ?? PIN_CARD_WIDTH;
  const gap = opts.gap ?? PIN_GAP;
  const columnY = new Array(columns).fill(origin.y);
  return heights.map((h) => {
    let col = 0;
    for (let c = 1; c < columns; c++) if (columnY[c] < columnY[col]) col = c;
    const pos = { x: origin.x + col * (cardWidth + gap), y: columnY[col] };
    columnY[col] += h + gap;
    return pos;
  });
}

interface Placed {
  position_x: number;
  position_y: number;
  width: number | null;
  height: number | null;
}

/** Where a new cluster starts: clear of everything already on the board, to its right. */
export function clusterOrigin(existing: Placed[], gap = 160): { x: number; y: number } {
  if (!existing.length) return { x: 100, y: 100 };
  let right = -Infinity;
  let top = Infinity;
  for (const item of existing) {
    right = Math.max(right, item.position_x + (item.width ?? 0));
    top = Math.min(top, item.position_y);
  }
  return { x: Math.round(right + gap), y: Math.round(top) };
}

/** Image card content for one pin. `url` is our stored copy, or the pin's own image when storing failed. */
export function pinItemContent(pin: PinterestPin, url: string, boardUrl: string, mimeType?: string | null): Record<string, unknown> {
  const label = pin.title || pin.description.slice(0, 120) || 'Pinterest pin';
  return {
    url,
    alt: label,
    ...(pin.title || pin.description ? { caption: pin.title || pin.description } : {}),
    ...(mimeType ? { mimeType } : {}),
    source_url: pin.pinUrl,
    original_url: pin.originalUrl ?? pin.image.url,
    source: 'pinterest',
    pinterest: { pin_id: pin.id, board_url: boardUrl, link: pin.link, is_video: pin.isVideo },
  };
}

// ── Partnr ───────────────────────────────────────────────────────

/**
 * What read_url adds for the model when the page it read is a Pinterest board
 * (or a pin.it link whose text reads like one): the text it got is only the
 * board's header, and where the pins actually come from. Null for anything else.
 */
export function pinterestCollectionNote(rawUrl: string, pageText: string, importTool: string | null): string | null {
  const board = pinterestBoardFromUrl(rawUrl);
  const looksLikeBoard = board || (isPinterestShortLink(rawUrl) && /\b\d[\d,.]*\s*pins\b/i.test(pageText));
  if (!looksLikeBoard) return null;
  const how = importTool
    ? `To bring its pins onto the canvas, call ${importTool} with this URL.`
    : 'To bring its pins onto the canvas, the user needs Pinterest Import switched on in Extensions; until then, ask them to paste the individual pin links (add_links works for those).';
  return `This is a Pinterest board. Its pins load with JavaScript, so the text below is only the board's header and does not list its pins. ${how} Never stand in for the board's pins with similar images from web_search.`;
}
